import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { API } from "../App";
import { toast } from "sonner";
import { BarChart3, Download, Users, CalendarCheck, Copy, Loader2, MapPin, DollarSign, CalendarClock } from "lucide-react";

const STATUS_OPTIONS = ["draft", "submitted", "under_review", "accepted", "waitlisted", "enrolled", "rejected", "withdrawn"];

function toCsv(rows, columns) {
  const header = columns.map(c => `"${c.label}"`).join(",");
  const body = rows.map(r => columns.map(c => {
    const v = r[c.key];
    const s = v === null || v === undefined ? "" : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  }).join(",")).join("\n");
  return header + "\n" + body;
}

function downloadCsv(filename, rows, columns) {
  if (!rows.length) { toast.error("Nothing to export"); return; }
  const blob = new Blob([toCsv(rows, columns)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const SummaryPill = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid={testid}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-lg font-bold text-slate-900">{value}</p>
  </div>
);

const ReportsTab = () => {
  const [view, setView] = useState("participants");
  const [programs, setPrograms] = useState([]);
  const [fundingSources, setFundingSources] = useState({});

  useEffect(() => {
    fetch(`${API}/programs`).then(r => r.json()).then(d => setPrograms(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/admin/funding-sources`, { credentials: "include" }).then(r => r.json()).then(d => setFundingSources(d.sources || {})).catch(() => {});
  }, []);

  // ── Participants report ────────────────────────────────────────
  const [filters, setFilters] = useState({ status: "all", funding_source: "all", state: "", program_id: "all", date_from: "", date_to: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const runParticipants = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filters.status !== "all") p.set("status", filters.status);
      if (filters.funding_source !== "all") p.set("funding_source", filters.funding_source);
      if (filters.state.trim()) p.set("state", filters.state.trim());
      if (filters.program_id !== "all") p.set("program_id", filters.program_id);
      if (filters.date_from) p.set("date_from", filters.date_from);
      if (filters.date_to) p.set("date_to", filters.date_to);
      const res = await fetch(`${API}/admin/reports/participants?${p.toString()}`, { credentials: "include" });
      const d = await res.json();
      setRows(d.rows || []);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };
  useEffect(() => { runParticipants(); }, []); // eslint-disable-line

  const summary = useMemo(() => {
    const byFunding = {}, byReferral = {}, byState = {};
    rows.forEach(r => {
      const f = r.funding_source_label || "Untagged"; byFunding[f] = (byFunding[f] || 0) + 1;
      const ref = r.referral_source || "Unknown"; byReferral[ref] = (byReferral[ref] || 0) + 1;
      const st = r.state || "Unknown"; byState[st] = (byState[st] || 0) + 1;
    });
    const top = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]).slice(0, 5);
    return { byFunding: top(byFunding), byReferral: top(byReferral), byState: top(byState) };
  }, [rows]);

  const participantCols = [
    { key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone" },
    { key: "state", label: "State" }, { key: "zip", label: "ZIP" }, { key: "county", label: "County" },
    { key: "referral_source", label: "Referral Source" }, { key: "program", label: "Program" },
    { key: "status", label: "Status" }, { key: "funding_source_label", label: "Funding Source" },
    { key: "funding_workforce_board", label: "Workforce Board" }, { key: "post_training_status", label: "Outcome" },
    { key: "employer", label: "Employer" }, { key: "applied_at", label: "Applied At" }, { key: "received_at", label: "Received At" },
  ];

  // ── Attendance report ──────────────────────────────────────────
  const [attFilters, setAttFilters] = useState({ program_id: "all", date_from: "", date_to: "" });
  const [attRows, setAttRows] = useState([]);
  const [attLoading, setAttLoading] = useState(false);
  const runAttendance = async () => {
    setAttLoading(true);
    try {
      const p = new URLSearchParams();
      if (attFilters.program_id !== "all") p.set("program_id", attFilters.program_id);
      if (attFilters.date_from) p.set("date_from", attFilters.date_from);
      if (attFilters.date_to) p.set("date_to", attFilters.date_to);
      const res = await fetch(`${API}/admin/reports/attendance?${p.toString()}`, { credentials: "include" });
      const d = await res.json();
      setAttRows(d.rows || []);
    } catch { toast.error("Failed to load attendance"); }
    finally { setAttLoading(false); }
  };
  const attCols = [{ key: "name", label: "Name" }, { key: "program", label: "Program" }, { key: "date", label: "Date" }, { key: "attended", label: "Attended" }];

  // ── Duplicates ────────────────────────────────────────────────
  const [dupGroups, setDupGroups] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);
  const runDuplicates = async () => {
    setDupLoading(true);
    try {
      const res = await fetch(`${API}/admin/reports/duplicates`, { credentials: "include" });
      const d = await res.json();
      setDupGroups(d.groups || []);
    } catch { toast.error("Failed to load duplicates"); }
    finally { setDupLoading(false); }
  };

  // ── Check-ins due ─────────────────────────────────────────────
  const [checkins, setCheckins] = useState([]);
  const [ciLoading, setCiLoading] = useState(false);
  const [ciWindow, setCiWindow] = useState("30");
  const runCheckins = async (win) => {
    setCiLoading(true);
    try {
      const res = await fetch(`${API}/admin/checkins/due?window_days=${win || ciWindow}`, { credentials: "include" });
      const d = await res.json();
      setCheckins(d.rows || []);
    } catch { toast.error("Failed to load check-ins"); }
    finally { setCiLoading(false); }
  };

  const tabBtn = (id, label, Icon) => (
    <button onClick={() => { setView(id); if (id === "attendance" && !attRows.length) runAttendance(); if (id === "duplicates" && !dupGroups.length) runDuplicates(); if (id === "checkins" && !checkins.length) runCheckins(); }}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${view === id ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}
      data-testid={`report-tab-${id}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="space-y-5" data-testid="reports-tab">
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <BarChart3 className="w-5 h-5 text-brand-navy" /> Reports &amp; Export
          </CardTitle>
          <p className="text-sm text-slate-500">Build custom participant, attendance, and data-quality reports — then export to CSV.</p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {tabBtn("participants", "Participants", Users)}
            {tabBtn("attendance", "Attendance", CalendarCheck)}
            {tabBtn("checkins", "Check-ins Due", CalendarClock)}
            {tabBtn("duplicates", "Duplicates", Copy)}
          </div>
        </CardContent>
      </Card>

      {/* PARTICIPANTS */}
      {view === "participants" && (
        <Card className="card-surface" data-testid="participants-report">
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                <SelectTrigger className="input-base" data-testid="report-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.funding_source} onValueChange={(v) => setFilters(f => ({ ...f, funding_source: v }))}>
                <SelectTrigger className="input-base" data-testid="report-filter-funding"><SelectValue placeholder="Funding" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All funding</SelectItem>
                  {Object.entries(fundingSources).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.program_id} onValueChange={(v) => setFilters(f => ({ ...f, program_id: v }))}>
                <SelectTrigger className="input-base" data-testid="report-filter-program"><SelectValue placeholder="Program" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All programs</SelectItem>
                  {programs.map(p => <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="State (e.g. IL)" value={filters.state} onChange={(e) => setFilters(f => ({ ...f, state: e.target.value }))} className="input-base" data-testid="report-filter-state" />
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} className="input-base" data-testid="report-filter-from" />
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} className="input-base" data-testid="report-filter-to" />
            </div>
            <div className="flex gap-2">
              <Button className="btn-primary gap-2" onClick={runParticipants} disabled={loading} data-testid="run-participants-report">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />} Run report
              </Button>
              <Button variant="outline" className="btn-outline gap-2" onClick={() => downloadCsv(`participants_${Date.now()}.csv`, rows, participantCols)} data-testid="export-participants-csv">
                <Download className="w-4 h-4" /> Export CSV
              </Button>
              <span className="ml-auto self-center text-sm text-slate-500" data-testid="participants-count">{rows.length} records</span>
            </div>

            {/* Summary cards */}
            <div className="grid md:grid-cols-3 gap-4 pt-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> By Funding</p>
                <div className="grid grid-cols-2 gap-2">{summary.byFunding.map(([k, v]) => <SummaryPill key={k} label={k} value={v} testid={`funding-pill-${k}`} />)}</div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">By Referral Source</p>
                <div className="grid grid-cols-2 gap-2">{summary.byReferral.map(([k, v]) => <SummaryPill key={k} label={k} value={v} />)}</div>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> By State</p>
                <div className="grid grid-cols-2 gap-2">{summary.byState.map(([k, v]) => <SummaryPill key={k} label={k} value={v} />)}</div>
              </div>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[480px] overflow-y-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>State</TableHead><TableHead>Program</TableHead>
                  <TableHead>Status</TableHead><TableHead>Funding</TableHead><TableHead>Referral</TableHead><TableHead>Outcome</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.slice(0, 200).map((r, i) => (
                    <TableRow key={i} data-testid={`participant-row-${i}`}>
                      <TableCell className="font-medium text-slate-800">{r.name}</TableCell>
                      <TableCell>{r.state}</TableCell>
                      <TableCell>{r.program || "—"}</TableCell>
                      <TableCell><Badge variant="outline">{(r.status || "").replace(/_/g, " ")}</Badge></TableCell>
                      <TableCell>{r.funding_source_label || <span className="text-slate-400">Untagged</span>}</TableCell>
                      <TableCell>{r.referral_source || "—"}</TableCell>
                      <TableCell>{r.post_training_status || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {rows.length > 200 && <p className="text-xs text-slate-400">Showing first 200 rows. Export CSV for the full {rows.length}.</p>}
          </CardContent>
        </Card>
      )}

      {/* ATTENDANCE */}
      {view === "attendance" && (
        <Card className="card-surface" data-testid="attendance-report">
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Select value={attFilters.program_id} onValueChange={(v) => setAttFilters(f => ({ ...f, program_id: v }))}>
                <SelectTrigger className="input-base" data-testid="att-filter-program"><SelectValue placeholder="Program" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All programs</SelectItem>
                  {programs.map(p => <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="date" value={attFilters.date_from} onChange={(e) => setAttFilters(f => ({ ...f, date_from: e.target.value }))} className="input-base" data-testid="att-filter-from" />
              <Input type="date" value={attFilters.date_to} onChange={(e) => setAttFilters(f => ({ ...f, date_to: e.target.value }))} className="input-base" data-testid="att-filter-to" />
              <Button className="btn-primary gap-2" onClick={runAttendance} disabled={attLoading} data-testid="run-attendance-report">
                {attLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-4 h-4" />} Run
              </Button>
            </div>
            <div className="flex">
              <Button variant="outline" className="btn-outline gap-2" onClick={() => downloadCsv(`attendance_${Date.now()}.csv`, attRows, attCols)} data-testid="export-attendance-csv">
                <Download className="w-4 h-4" /> Export CSV
              </Button>
              <span className="ml-auto self-center text-sm text-slate-500" data-testid="attendance-count">{attRows.length} records</span>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[480px] overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Program</TableHead><TableHead>Date</TableHead><TableHead>Attended</TableHead></TableRow></TableHeader>
                <TableBody>
                  {attRows.map((r, i) => (
                    <TableRow key={i} data-testid={`attendance-row-${i}`}>
                      <TableCell className="font-medium text-slate-800">{r.name}</TableCell>
                      <TableCell>{r.program}</TableCell>
                      <TableCell>{r.date}</TableCell>
                      <TableCell><Badge className={r.attended === "Yes" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}>{r.attended}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {!attRows.length && !attLoading && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">No attendance records for these filters.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CHECK-INS DUE */}
      {view === "checkins" && (
        <Card className="card-surface" data-testid="checkins-report">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center gap-3">
              <Select value={ciWindow} onValueChange={(v) => { setCiWindow(v); runCheckins(v); }}>
                <SelectTrigger className="input-base w-48" data-testid="checkin-window"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="0">Overdue only</SelectItem>
                  <SelectItem value="30">Due within 30 days</SelectItem>
                  <SelectItem value="60">Due within 60 days</SelectItem>
                  <SelectItem value="90">Due within 90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button className="btn-primary gap-2" onClick={() => runCheckins()} disabled={ciLoading} data-testid="run-checkins-report">
                {ciLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Refresh
              </Button>
              <Button variant="outline" className="btn-outline gap-2" onClick={() => downloadCsv(`checkins_due_${Date.now()}.csv`, checkins, [{ key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "employer", label: "Employer" }, { key: "milestone", label: "Milestone" }, { key: "due_date", label: "Due Date" }, { key: "overdue", label: "Overdue" }])} data-testid="export-checkins-csv">
                <Download className="w-4 h-4" /> Export CSV
              </Button>
              <span className="ml-auto self-center text-sm text-slate-500" data-testid="checkins-count">{checkins.length} due</span>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[480px] overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Employer</TableHead><TableHead>Milestone</TableHead><TableHead>Due Date</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {checkins.map((r, i) => (
                    <TableRow key={i} data-testid={`checkin-due-row-${i}`}>
                      <TableCell className="font-medium text-slate-800">{r.name}</TableCell>
                      <TableCell>{r.employer || "—"}</TableCell>
                      <TableCell>{r.milestone === "6mo" ? "6-Month" : r.milestone === "1yr" ? "1-Year" : "2-Year"}</TableCell>
                      <TableCell>{r.due_date}</TableCell>
                      <TableCell><Badge className={r.overdue ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}>{r.overdue ? "Overdue" : "Upcoming"}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {!checkins.length && !ciLoading && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No check-ins due in this window. 🎉</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* DUPLICATES */}
      {view === "duplicates" && (
        <Card className="card-surface" data-testid="duplicates-report">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center">
              <Button className="btn-primary gap-2" onClick={runDuplicates} disabled={dupLoading} data-testid="run-duplicates-report">
                {dupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />} Scan for duplicates
              </Button>
              <span className="ml-auto self-center text-sm text-slate-500" data-testid="duplicates-count">{dupGroups.length} potential duplicate groups</span>
            </div>
            <div className="space-y-3">
              {dupGroups.map((g, i) => (
                <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid={`dup-group-${i}`}>
                  <p className="text-xs text-amber-800 mb-2">Matched on <span className="font-bold uppercase">{g.match_on}</span>: <span className="font-mono">{g.value}</span></p>
                  <div className="space-y-1">
                    {g.users.map(u => (
                      <div key={u.user_id} className="flex items-center gap-2 text-sm">
                        <Badge variant="outline" className="border-slate-300">{u.role}</Badge>
                        <span className="font-medium text-slate-800">{u.name}</span>
                        <span className="text-slate-500">{u.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {!dupGroups.length && !dupLoading && <p className="text-center text-slate-400 py-6">No potential duplicates found. 🎉</p>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsTab;
